import { intents } from '@drone-js/theme';
import { Direction } from '../contexts/session/Locale.js';
import { toCxn, toCx } from './classname.js';
import { maskColors } from './gradient.js';
import { cursorMap, floatMap } from './mappers.mappings.js';
import { replaceAllDots } from './stringFormatter.js';
import { selectToken } from './tokenSelector.js';

var toClassList = function toClassList(className) {
  return className.split(' ').filter(Boolean);
};
var mapFloat = function mapFloat(float, direction) {
  if (float === 'start') return direction === Direction.LTR ? 'left' : 'right';
  if (float === 'end') return direction === Direction.LTR ? 'right' : 'left';
  return float;
};
var toMapper = function toMapper(record) {
  return function (value, _) {
    return value !== undefined ? record[value] : undefined;
  };
};
var mapBorder = function mapBorder() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      borderWidth: undefined,
      sides: []
    },
    borderWidth = _ref.borderWidth,
    sides = _ref.sides;
  var _ref2 = arguments.length > 1 ? arguments[1] : undefined,
    screen = _ref2.screen,
    isButton = _ref2.isButton;
  var result = isButton && !borderWidth ? '0' : borderWidth;
  var className = toCxn('border', result, sides, undefined, screen, true);
  return toClassList(className);
};
var mapBorderRadius = function mapBorderRadius() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      borderRadius: undefined,
      corners: [],
      screen: undefined
    },
    borderRadius = _ref3.borderRadius,
    corners = _ref3.corners,
    screen = _ref3.screen;
  var _ = arguments.length > 1 ? arguments[1] : undefined;
  return toClassList(toCxn('rounded', borderRadius, corners, false, screen, true));
};
var isThemableToken = function isThemableToken(token) {
  var intentColor = ['base', 'state'];
  return intentColor.some(function (color) {
    return token.startsWith(color);
  });
};
var mapColorTokenToClass = function mapColorTokenToClass(token, colorType, intent) {
  if (!token) return undefined;
  var t = isThemableToken(token) && intent ? "".concat(intent, "-").concat(token) : token;
  return toCx(colorType, t);
};
var getDefaultBackground = function getDefaultBackground(backgroundColor, isButton) {
  var bgDefaultButton = 'base.transparent';
  return isButton && !backgroundColor ? bgDefaultButton : backgroundColor;
};
var mapBackground = function mapBackground(backgroundColor, _ref4) {
  var isButton = _ref4.isButton,
    intent = _ref4.intent;
  var realBg = getDefaultBackground(backgroundColor, !!isButton);
  var bg = typeof realBg === 'string' ? realBg : undefined;
  if (!bg) return undefined;
  return mapColorTokenToClass(bg, 'bg', intent);
};
var mapTextColor = function mapTextColor(textColor, colorType, options) {
  if (!textColor) return undefined;
  if (textColor === 'inherit') return toCx(colorType, 'inherit');
  return mapColorTokenToClass(textColor, colorType, options.intent);
};
var mapBorderColor = function mapBorderColor(borderColor, options) {
  if (!borderColor) return undefined;
  return mapColorTokenToClass(borderColor, 'border', options.intent);
};
var mapCursor = function mapCursor(cursor, _ref5) {
  var isButton = _ref5.isButton;
  if (isButton && !cursor) {
    return cursorMap.pointer;
  }
  if (cursor) {
    return cursorMap[cursor];
  }
  return undefined;
};
var floatMapper = function floatMapper(float, _ref6) {
  var direction = _ref6.direction;
  if (float === undefined) {
    return undefined;
  }
  var res = mapFloat(float, direction);
  if (res === undefined) return undefined;
  return floatMap[res];
};
var isStaticToken = function isStaticToken(token) {
  var staticColor = ['product', 'feedback'];
  var isProductOrFeedbackToken = staticColor.some(function (color) {
    return token.startsWith(color);
  });
  var tokenName = token.replace(/product.on.|feedback.on.|product.|feedback./g, '');
  var isIntent = intents.some(function (intent) {
    return tokenName.startsWith(intent);
  });
  return isProductOrFeedbackToken && isIntent;
};
function transformStaticToken(token) {
  var parts = token.split('.');

  // Remove the first part
  parts.shift();
  var intentName = parts[0] === 'on' ? parts[1] : parts[0];
  var themableToken = parts.join('.').replace(intentName, 'intent');
  return "".concat(intentName, ".base.").concat(themableToken);
}
var resolveTokenValue = function resolveTokenValue(token, intent) {
  if (isThemableToken(token) && intent) {
    return "".concat(intent, "-").concat(token);
  }
  if (isStaticToken(token)) {
    return transformStaticToken(token);
  }
  return token;
};
var mapColorTokenToCssVariable = function mapColorTokenToCssVariable(token, colorType, intent) {
  if (!token) return undefined;
  var resolvedToken = resolveTokenValue(token, intent);
  return "--ads-".concat(colorType, "-").concat(replaceAllDots(resolvedToken));
};
var mapGradient = function mapGradient(gradient, dir, theme) {
  var _gradient$angle;
  var intent = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'generic';
  if (!gradient || typeof gradient === 'string') return undefined;
  var angle = (_gradient$angle = gradient.angle) !== null && _gradient$angle !== void 0 ? _gradient$angle : 180;
  var directionalAngle = dir === Direction.LTR ? angle : 360 - angle;
  return "linear-gradient(".concat(directionalAngle, "deg, ").concat(gradient.stops.map(function (stop, index) {
    var _backgroundColor, _position;
    var token = (_backgroundColor = stop.backgroundColor) !== null && _backgroundColor !== void 0 ? _backgroundColor : stop;
    var distance = (_position = stop.position) !== null && _position !== void 0 ? _position : "".concat(Math.round(index / (gradient.stops.length - 1) * 100), "%");
    var maskColor = maskColors[token];
    if (maskColor) return "".concat(maskColor, " ").concat(distance);

    // Theme provided, use selectToken, can be removed once Box CSS experiment is integrated
    if (theme) return "".concat(selectToken(theme.background, token), " ").concat(distance);
    return "var(".concat(mapColorTokenToCssVariable(token, 'bg', intent), ") ").concat(distance);
  }), ")");
};
var mapBackgroundColor = function mapBackgroundColor(backgroundType, _ref7, mappedProps) {
  var _mapGradient;
  var isLtr = _ref7.isLtr,
    theme = _ref7.theme,
    intent = _ref7.intent;
  return {
    backgroundImage: [mappedProps.backgroundImage, (_mapGradient = mapGradient(backgroundType, isLtr ? Direction.LTR : Direction.RTL, theme, intent)) !== null && _mapGradient !== void 0 ? _mapGradient : backgroundType].filter(function (b) {
      return b;
    }).join(',')
  };
};
var mapBackgroundImage = function mapBackgroundImage(backgroundImage, _, mappedProps) {
  return {
    backgroundImage: [mappedProps.backgroundImage, backgroundImage].filter(function (b) {
      return b;
    }).join(',')
  };
};
var mapStart = function mapStart(start, _ref8, mappedProps) {
  var isLtr = _ref8.isLtr;
  if (start === undefined) return {};
  if (isLtr && mappedProps.left === undefined) return {
    left: start
  };
  if (!isLtr && mappedProps.right === undefined) return {
    right: start
  };
  return {};
};
var mapEnd = function mapEnd(end, _ref9, mappedProps) {
  var isLtr = _ref9.isLtr;
  if (end === undefined) return {};
  if (isLtr && mappedProps.right === undefined) return {
    right: end
  };
  if (!isLtr && mappedProps.left === undefined) return {
    left: end
  };
  return {};
};
var mapSafeArea = function mapSafeArea(safeArea) {
  return [(safeArea === null || safeArea === void 0 ? void 0 : safeArea.top) && 'pt-safe', (safeArea === null || safeArea === void 0 ? void 0 : safeArea.bottom) && 'pb-safe', (safeArea === null || safeArea === void 0 ? void 0 : safeArea.left) && 'pl-safe', (safeArea === null || safeArea === void 0 ? void 0 : safeArea.right) && 'pr-safe'].filter(Boolean);
};

export { floatMapper, getDefaultBackground, isStaticToken, isThemableToken, mapBackground, mapBackgroundColor, mapBackgroundImage, mapBorder, mapBorderColor, mapBorderRadius, mapColorTokenToClass, mapColorTokenToCssVariable, mapCursor, mapEnd, mapFloat, mapGradient, mapSafeArea, mapStart, mapTextColor, toMapper };
//# sourceMappingURL=mappers.util.js.map
